import React, { useState, useEffect } from 'react';
import PageHeader from '../components/PageHeader';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/api';

function QuotesAnalytics() {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('month');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedMonth, setSelectedMonth] = useState('2026-04');
  const [selectedYear, setSelectedYear] = useState('2026');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [stats, setStats] = useState({
    currentPeriod: {
      totalQuotes: 0,
      draftQuotes: 0,
      pendingQuotes: 0,
      approvedQuotes: 0,
      rejectedQuotes: 0,
      totalValue: 0,
      draftValue: 0,
      pendingValue: 0,
      approvedValue: 0,
      rejectedValue: 0
    },
    previousPeriod: {
      totalQuotes: 0,
      draftQuotes: 0,
      pendingQuotes: 0,
      approvedQuotes: 0,
      rejectedQuotes: 0,
      totalValue: 0,
      draftValue: 0,
      pendingValue: 0,
      approvedValue: 0,
      rejectedValue: 0
    },
    statusBreakdown: {},
    monthlyTrend: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, [viewMode, selectedDate, selectedMonth, selectedYear, customStartDate, customEndDate]);

  const getDateRange = () => {
    let currentStart, currentEnd, prevStart, prevEnd, label;
    const today = new Date();

    switch (viewMode) {
      case 'day':
        currentStart = new Date(selectedDate);
        currentStart.setHours(0, 0, 0, 0);
        currentEnd = new Date(selectedDate);
        currentEnd.setHours(23, 59, 59, 999);
        prevStart = new Date(currentStart);
        prevStart.setDate(prevStart.getDate() - 1);
        prevEnd = new Date(prevStart);
        prevEnd.setHours(23, 59, 59, 999);
        label = currentStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
        break;

      case 'week':
        currentStart = new Date(selectedDate);
        const dayOfWeek = currentStart.getDay();
        currentStart.setDate(currentStart.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
        currentStart.setHours(0, 0, 0, 0);
        currentEnd = new Date(currentStart);
        currentEnd.setDate(currentEnd.getDate() + 6);
        currentEnd.setHours(23, 59, 59, 999);
        prevStart = new Date(currentStart);
        prevStart.setDate(prevStart.getDate() - 7);
        prevEnd = new Date(currentEnd);
        prevEnd.setDate(prevEnd.getDate() - 7);
        label = `Week of ${currentStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`;
        break;

      case 'month':
        const [year, month] = selectedMonth.split('-');
        currentStart = new Date(year, month - 1, 1);
        currentEnd = new Date(year, month, 0, 23, 59, 59, 999);
        prevStart = new Date(currentStart);
        prevStart.setMonth(prevStart.getMonth() - 1);
        prevEnd = new Date(prevStart.getFullYear(), prevStart.getMonth() + 1, 0, 23, 59, 59, 999);
        label = currentStart.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
        break;

      case 'quarter':
        const currentQuarter = Math.floor((today.getMonth()) / 3);
        currentStart = new Date(selectedYear, currentQuarter * 3, 1);
        currentEnd = new Date(selectedYear, currentQuarter * 3 + 3, 0, 23, 59, 59, 999);
        prevStart = new Date(currentStart);
        prevStart.setMonth(prevStart.getMonth() - 3);
        prevEnd = new Date(prevStart.getFullYear(), prevStart.getMonth() + 3, 0, 23, 59, 59, 999);
        label = `Q${currentQuarter + 1} ${selectedYear}`;
        break;

      case 'year':
        currentStart = new Date(selectedYear, 0, 1);
        currentEnd = new Date(selectedYear, 11, 31, 23, 59, 59, 999);
        prevStart = new Date(parseInt(selectedYear) - 1, 0, 1);
        prevEnd = new Date(parseInt(selectedYear) - 1, 11, 31, 23, 59, 59, 999);
        label = selectedYear;
        break;

      case 'custom':
        if (customStartDate && customEndDate) {
          currentStart = new Date(customStartDate);
          currentEnd = new Date(customEndDate);
          currentEnd.setHours(23, 59, 59, 999);
          const daysDiff = Math.floor((currentEnd - currentStart) / (1000 * 60 * 60 * 24));
          prevStart = new Date(currentStart);
          prevStart.setDate(prevStart.getDate() - daysDiff - 1);
          prevEnd = new Date(prevStart);
          prevEnd.setDate(prevEnd.getDate() + daysDiff);
          label = `${currentStart.toLocaleDateString('en-GB')} - ${currentEnd.toLocaleDateString('en-GB')}`;
        } else {
          return null;
        }
        break;

      default:
        return null;
    }

    return { currentStart, currentEnd, prevStart, prevEnd, label };
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const dateRange = getDateRange();
      if (!dateRange) {
        setLoading(false);
        return;
      }

      const quotesRes = await apiFetch('/api/quotes');
      const allQuotes = await quotesRes.json();

      // For now, use all quotes
      const currentQuotes = allQuotes;
      const previousQuotes = [];

      const currentMetrics = calculateMetrics(currentQuotes);
      const previousMetrics = calculateMetrics(previousQuotes);

      const statusBreakdown = {};
      currentQuotes.forEach(quote => {
        const status = quote.status || 'Draft';
        if (!statusBreakdown[status]) {
          statusBreakdown[status] = { count: 0, value: 0 };
        }
        statusBreakdown[status].count++;
        statusBreakdown[status].value += parseFloat(quote.total_amount || 0);
      });

      const monthlyTrend = calculateMonthlyTrend(allQuotes, new Date());

      setStats({
        currentPeriod: currentMetrics,
        previousPeriod: previousMetrics,
        statusBreakdown,
        monthlyTrend
      });
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setLoading(false);
    }
  };

  const calculateMetrics = (quotes) => {
    const draft = quotes.filter(q => q.status === 'Draft' || q.status === 'Pending');
    const approved = quotes.filter(q => q.status === 'Approved' || q.status === 'Accepted');
    const rejected = quotes.filter(q => q.status === 'Rejected' || q.status === 'Declined');

    return {
      totalQuotes: quotes.length,
      draftQuotes: draft.length,
      approvedQuotes: approved.length,
      rejectedQuotes: rejected.length,
      totalValue: quotes.reduce((sum, q) => sum + parseFloat(q.total_amount || 0), 0),
      draftValue: draft.reduce((sum, q) => sum + parseFloat(q.total_amount || 0), 0),
      approvedValue: approved.reduce((sum, q) => sum + parseFloat(q.total_amount || 0), 0),
      rejectedValue: rejected.reduce((sum, q) => sum + parseFloat(q.total_amount || 0), 0),
      winRate: quotes.length > 0 ? ((approved.length / quotes.length) * 100).toFixed(1) : 0
    };
  };

  const calculateMonthlyTrend = (allQuotes, currentMonth) => {
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const date = new Date(currentMonth);
      date.setMonth(date.getMonth() - i);
      months.push({
        label: date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }),
        quotes: allQuotes.length,
        value: allQuotes.reduce((sum, q) => sum + parseFloat(q.total_amount || 0), 0) / 6
      });
    }
    return months;
  };

  const getTrendArrow = (current, previous) => {
    if (current > previous) return { icon: '↑', color: 'text-green-600' };
    if (current < previous) return { icon: '↓', color: 'text-red-600' };
    return { icon: '→', color: 'text-blue-600' };
  };

  const ComparisonCard = ({ title, currentCount, previousCount, currentValue, previousValue }) => {
    const countTrend = getTrendArrow(currentCount, previousCount);
    const valueTrend = getTrendArrow(currentValue, previousValue);

    return (
      <div className="bg-white rounded-lg shadow p-4 border-t-4 border-blue-500">
        <h3 className="text-blue-600 font-semibold mb-3 text-sm">{title}</h3>
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-600">This Period</span>
            <span className={`font-bold ${countTrend.color}`}>
              {currentCount} {countTrend.icon}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-600">Last Period</span>
            <span className="font-semibold text-gray-700">{previousCount}</span>
          </div>
          <div className="flex justify-between items-center pt-2 border-t">
            <span className="text-xs text-gray-600">This Period</span>
            <span className={`font-bold ${valueTrend.color}`}>
              £{currentValue.toLocaleString()} {valueTrend.icon}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-600">Last Period</span>
            <span className="font-semibold text-gray-700">
              £{previousValue.toLocaleString()}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const SummaryCard = ({ title, count, value }) => (
    <div className="bg-white rounded-lg shadow p-4 border-t-4 border-blue-500">
      <h3 className="text-blue-600 font-semibold mb-3 text-sm">{title}</h3>
      <div className="text-2xl font-bold text-gray-900">{count}</div>
      <div className="text-sm text-gray-600 mt-1">£{value.toLocaleString()}</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <PageHeader 
        title="Quotes Analytics"
        subtitle="Pipeline & Conversion Insights"
        backButton
        onBack={() => navigate('/dashboard')}
        backTitle="Back to Dashboard"
      />

      {/* Filter Bar */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-full mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm text-gray-600 font-semibold">
              {getDateRange()?.label || 'Select Date Range'}
            </div>
            <div className="text-sm text-gray-600">
              Today's Date - {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            </div>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              {['day', 'week', 'month', 'quarter', 'year', 'custom'].map(mode => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={`px-3 py-1 rounded text-sm font-medium transition ${
                    viewMode === mode ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {mode.charAt(0).toUpperCase() + mode.slice(1)}
                </button>
              ))}
            </div>

            {viewMode === 'day' && (
              <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg" />
            )}
            {viewMode === 'week' && (
              <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg" />
            )}
            {viewMode === 'month' && (
              <input type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg" />
            )}
            {(viewMode === 'quarter' || viewMode === 'year') && (
              <select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg">
                {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            )}
            {viewMode === 'custom' && (
              <>
                <input type="date" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg" />
                <span>to</span>
                <input type="date" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg" />
              </>
            )}

            <div className="ml-auto text-xs text-gray-500">* All values are excluding VAT</div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-full mx-auto px-6 py-6">
        
        {loading && (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">⏳</div>
            <div className="text-gray-600">Loading analytics data...</div>
          </div>
        )}

        {!loading && (
          <>
        {/* Row 1 - Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <SummaryCard title="Total Quotes" count={stats.currentPeriod.totalQuotes} value={stats.currentPeriod.totalValue} />
          <SummaryCard title="Quotes In Draft" count={stats.currentPeriod.draftQuotes} value={stats.currentPeriod.draftValue} />
          <SummaryCard title="Quotes Approved" count={stats.currentPeriod.approvedQuotes} value={stats.currentPeriod.approvedValue} />
          <SummaryCard title="Quotes Rejected" count={stats.currentPeriod.rejectedQuotes} value={stats.currentPeriod.rejectedValue} />
        </div>

        {/* Row 2 - Comparison Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <ComparisonCard title="Total Quotes" currentCount={stats.currentPeriod.totalQuotes} previousCount={stats.previousPeriod.totalQuotes}
            currentValue={stats.currentPeriod.totalValue} previousValue={stats.previousPeriod.totalValue} />
          <ComparisonCard title="Draft Quotes" currentCount={stats.currentPeriod.draftQuotes} previousCount={stats.previousPeriod.draftQuotes}
            currentValue={stats.currentPeriod.draftValue} previousValue={stats.previousPeriod.draftValue} />
          <ComparisonCard title="Approved Quotes" currentCount={stats.currentPeriod.approvedQuotes} previousCount={stats.previousPeriod.approvedQuotes}
            currentValue={stats.currentPeriod.approvedValue} previousValue={stats.previousPeriod.approvedValue} />
          <ComparisonCard title="Rejected Quotes" currentCount={stats.currentPeriod.rejectedQuotes} previousCount={stats.previousPeriod.rejectedQuotes}
            currentValue={stats.currentPeriod.rejectedValue} previousValue={stats.previousPeriod.rejectedValue} />
        </div>

        {/* Row 3 - Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Status Breakdown */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Quotes by Status</h3>
            <div className="space-y-3">
              {Object.entries(stats.statusBreakdown).map(([status, data]) => (
                <div key={status}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium text-gray-700">{status}</span>
                    <span className="font-bold text-gray-900">{data.count}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-green-500 h-2 rounded-full"
                      style={{ width: `${(data.count / stats.currentPeriod.totalQuotes) * 100}%` }}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Donut Chart */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Quote Distribution</h3>
            <div className="text-center">
              <div className="text-6xl font-bold text-gray-900">{stats.currentPeriod.totalQuotes}</div>
              <div className="text-sm text-gray-600 mt-2">Total Quotes</div>
              <div className="text-2xl font-bold text-green-600 mt-4">
                {stats.currentPeriod.winRate}%
              </div>
              <div className="text-xs text-gray-600">Win Rate</div>
            </div>
          </div>

          {/* Monthly Trend */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">6-Month Trend</h3>
            <div className="space-y-3">
              {stats.monthlyTrend.map((month, idx) => {
                const maxValue = Math.max(...stats.monthlyTrend.map(m => m.value), 1);
                return (
                  <div key={idx}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-medium text-gray-700">{month.label}</span>
                      <span className="text-green-600 font-semibold">£{month.value.toLocaleString()}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-green-500 h-2 rounded-full"
                        style={{ width: `${(month.value / maxValue) * 100}%` }}></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        </>
        )}

      </main>
    </div>
  );
}

export default QuotesAnalytics;
